import type { FormInstance } from '@arco-design/web-react';

import { type DisplayConfigItem, isAutoManagedDisplayRow, mergeManagedDisplayRows, parseDisplayConfig } from '@/delivery-type-plugin';
import type { PipelineContext } from '@/delivery-type-plugin';
import i18n from '@/i18n';

import { APP_INSTANCE_RESOURCE_CONFIGS } from '../resourceConfigs';
import { buildAppInstanceFreshDisplayConfig } from './index';
import {
  APP_INSTANCE_PRODUCT_CARD_EXCLUDED_DISPLAY_IDS,
  filterAppInstanceProductCardDisplayConfig,
  isAppInstanceProductCardDisplayId,
} from './productCard';

const t = i18n.t.bind(i18n);

const IMAGE_ROW_IDS_WITH_ARCHITECTURE_TAG = new Set(['app_image']);
const ARCHITECTURE_ROW_ID = 'architecture';

function isStandaloneArchitectureRow(item: { id: string; key?: string }) {
  if (IMAGE_ROW_IDS_WITH_ARCHITECTURE_TAG.has(item.id)) return false;
  if (item.id === ARCHITECTURE_ROW_ID) return true;
  return item.key === t('common:architecture');
}

function stripStandaloneArchitectureRow<T extends { id: string; key?: string }>(arr: T[]): T[] {
  return arr.filter((item) => !isStandaloneArchitectureRow(item));
}

function syncArchitectureTagOnImageRow<T extends { id: string; tag?: string }>(arr: T[], architecture: unknown) {
  const arch = String(architecture ?? '').trim();
  if (!arch) return;
  const imageRow = arr.find((item) => IMAGE_ROW_IDS_WITH_ARCHITECTURE_TAG.has(item.id));
  if (imageRow) {
    imageRow.tag = arch;
  }
}

/** 应用实例：移除独立架构行，并写入镜像 tag；再按商品卡片规则过滤 */
export function finalizeAppInstanceSubmitDisplayConfig(arr: unknown[], architecture?: unknown): DisplayConfigItem[] {
  const items = (Array.isArray(arr) ? arr : parseDisplayConfig(arr)) as DisplayConfigItem[];
  const next = stripStandaloneArchitectureRow(items);
  syncArchitectureTagOnImageRow(next, architecture);
  return filterAppInstanceProductCardDisplayConfig(next);
}

export function overlayFreshNonManagedRows(
  merged: DisplayConfigItem[],
  fresh: DisplayConfigItem[],
  resourceConfigs = APP_INSTANCE_RESOURCE_CONFIGS,
): DisplayConfigItem[] {
  const freshMap = new Map(fresh.map((row) => [row.id, row]));
  const next = merged.map((row) => {
    if (isAutoManagedDisplayRow(row.id, resourceConfigs)) return row;
    return freshMap.get(row.id) ?? row;
  });
  const usedIds = new Set(next.map((row) => row.id));
  for (const row of fresh) {
    if (!isAutoManagedDisplayRow(row.id, resourceConfigs) && !usedIds.has(row.id)) {
      next.push(row);
    }
  }
  return next;
}

/** innerForm + 当前 displayConfig → merge 后的商品卡片行 */
export function buildAppInstanceMergedDisplayConfig(innerForm: FormInstance, currentDisplayConfig: unknown, pipelineCtx: PipelineContext) {
  const innerFormValues = innerForm.getFieldsValue() as Record<string, unknown>;
  const fresh = filterAppInstanceProductCardDisplayConfig(buildAppInstanceFreshDisplayConfig(innerForm, pipelineCtx));
  const merged = mergeManagedDisplayRows({
    current: parseDisplayConfig(currentDisplayConfig).filter((row) => isAppInstanceProductCardDisplayId(row.id)),
    fresh,
    resourceConfigs: APP_INSTANCE_RESOURCE_CONFIGS,
    innerFormValues,
    displayMode: 'productCard',
    excludedIds: APP_INSTANCE_PRODUCT_CARD_EXCLUDED_DISPLAY_IDS,
    filterRow: (row) => isAppInstanceProductCardDisplayId(row.id) && String(row.value ?? '').trim() !== '',
  });
  return filterAppInstanceProductCardDisplayConfig(overlayFreshNonManagedRows(merged, fresh));
}
